import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LifeBuoy, ChevronDown, Send, Loader2 } from "lucide-react";
import aapi, { fmtDate, formatApiError } from "./aapi";
import { PageHeader, EmptyState, inputCls, selectCls } from "@/panel/components";
import { EASE } from "@/components/Motion";

const STATUS_TABS = ["all", "open", "pending", "answered", "closed"];
const TICKET_STYLES = {
  open: "border-amber-400/30 bg-amber-400/10 text-amber-300",
  pending: "border-sky-400/30 bg-sky-400/10 text-sky-300",
  answered: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
  closed: "border-white/15 bg-white/5 text-neutral-400",
};
const PRIORITY_STYLES = {
  low: "text-neutral-400",
  normal: "text-sky-300",
  high: "text-amber-300",
  urgent: "text-rose-300",
};

export default function AdminSupport() {
  const [tickets, setTickets] = useState(null);
  const [tab, setTab] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    aapi.get("/tickets", { params: { status: tab } }).then(({ data }) => setTickets(data)).catch(() => setTickets([]));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const sendReply = async (ticketId) => {
    if (reply.trim().length < 2) return;
    setBusy(true);
    try {
      await aapi.post(`/tickets/${ticketId}/reply`, { body: reply });
      toast.success("Reply sent — customer notified");
      setReply("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const updateTicket = async (ticketId, updates) => {
    try {
      await aapi.put(`/tickets/${ticketId}`, updates);
      toast.success("Ticket updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-support-page">
      <PageHeader title="Support Desk" sub="Reply fast, close faster." />

      <div className="mb-6 flex flex-wrap gap-2">
        {STATUS_TABS.map((s) => (
          <button key={s} onClick={() => setTab(s)} data-testid={`admin-tickets-tab-${s}`}
            className={`rounded-full border px-4 py-2 text-xs font-medium capitalize transition-all duration-300 ${
              tab === s ? "border-gold bg-gold/10 text-goldlight" : "border-white/10 text-neutral-400 hover:border-gold/40 hover:text-neutral-100"
            }`}>
            {s}
          </button>
        ))}
      </div>

      {tickets === null ? (
        <div className="flex justify-center py-24">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/20 border-t-gold" />
        </div>
      ) : tickets.length === 0 ? (
        <EmptyState icon={LifeBuoy} title="Inbox zero" sub="No tickets in this state." testid="admin-tickets-empty" />
      ) : (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
          className="space-y-3" data-testid="admin-tickets-list">
          {tickets.map((t) => {
            const open = openId === t.id;
            return (
              <div key={t.id} className="overflow-hidden rounded-2xl border border-white/10 bg-panel/80" data-testid={`admin-ticket-${t.ticket_id}`}>
                <button onClick={() => setOpenId(open ? null : t.id)} data-testid={`admin-ticket-toggle-${t.ticket_id}`}
                  className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left transition-colors hover:bg-white/[0.02]">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-mono text-xs text-gold/80">{t.ticket_id}</span>
                      <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${TICKET_STYLES[t.status] || TICKET_STYLES.open}`}>
                        {t.status}
                      </span>
                      <span className={`font-mono text-[10px] uppercase tracking-wider ${PRIORITY_STYLES[t.priority] || ""}`}>
                        {t.priority} priority
                      </span>
                    </div>
                    <div className="mt-1.5 truncate text-sm font-medium text-neutral-100">{t.subject}</div>
                    <div className="mt-0.5 font-mono text-[10px] text-neutral-600">{t.user_email} · {fmtDate(t.created_at)}</div>
                  </div>
                  <ChevronDown size={16} className={`shrink-0 text-neutral-500 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                </button>

                {open && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                    transition={{ duration: 0.4, ease: EASE }} className="border-t border-white/5 px-6 py-5">
                    <p className="text-sm leading-relaxed text-neutral-300">{t.message}</p>
                    {t.replies.map((r, i) => (
                      <div key={i} className="mt-4 rounded-xl border border-gold/15 bg-gold/[0.05] p-4">
                        <div className="font-mono text-[9px] tracking-[0.25em] text-gold/80">SUPPORT · {fmtDate(r.at)}</div>
                        <p className="mt-2 text-sm leading-relaxed text-neutral-300">{r.body}</p>
                      </div>
                    ))}

                    <div className="mt-6 grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Status</label>
                        <select value={t.status} onChange={(e) => updateTicket(t.ticket_id, { status: e.target.value })}
                          className={`${selectCls} mt-2`} data-testid={`ticket-status-${t.ticket_id}`}>
                          {["open", "pending", "answered", "closed"].map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Priority</label>
                        <select value={t.priority} onChange={(e) => updateTicket(t.ticket_id, { priority: e.target.value })}
                          className={`${selectCls} mt-2`} data-testid={`ticket-priority-${t.ticket_id}`}>
                          {["low", "normal", "high", "urgent"].map((p) => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mt-4 flex gap-3">
                      <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply — customer gets notified"
                        className={inputCls} data-testid={`ticket-reply-input-${t.ticket_id}`} />
                      <button onClick={() => sendReply(t.ticket_id)} disabled={busy} data-testid={`ticket-reply-send-${t.ticket_id}`}
                        className="flex shrink-0 items-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark px-5 text-sm font-bold text-black disabled:opacity-50">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}
